import { Flex, Theme, ThemePanel } from '@radix-ui/themes'
import { ManifestLink, useSWEffect } from '@remix-pwa/sw'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import moment from 'moment'
import * as React from 'react'
import 'moment/locale/de.js'
import 'moment/locale/el.js'
import 'moment/locale/es.js'
import 'moment/locale/fr.js'
import 'moment/locale/it.js'
import 'moment/locale/nl.js'
import 'moment/locale/pt.js'
import 'moment/locale/ru.js'
import 'moment/locale/tr.js'
import 'moment/locale/zh-cn.js'
import { useTranslation } from 'react-i18next'
import { useChangeLanguage } from 'remix-i18next/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'

import { appConfig } from '#app/app-config.ts'
import { ClientHintCheck } from '#app/components/common/client-hints'
import { AppleMobileWebApp } from '#app/components/seo/apple-mobile-web-app.tsx'
import { FavIcon } from '#app/components/seo/fav-icon'
import { MsApplication } from '#app/components/seo/ms-application.tsx'
import { Toast, ToastContainer } from '#app/components/ui/toast'
import i18nConfig from '#app/i18n'
import { type GetOrder } from '#app/models/ecommerce-providers/sylius/shop/fetch/order/get-order'
import { type Locale } from '#app/models/locale'
import { Footer } from '#app/routes/_layout/footer.tsx'
import { ModalUI } from '#app/routes/_layout/modal-ui.tsx'
import { Navbar } from '#app/routes/_layout/navbar.tsx'
import { type App, ROOT_APP_VIEW } from '#app/routes/_layout/route-config.ts'
import { SidebarUI } from '#app/routes/_layout/sidebar-ui.tsx'
import { CartProvider } from '#app/utils/client/cart-context.tsx'
import { useIsBot } from '#app/utils/hooks/use-is-bot.ts'
import { useNonce } from '#app/utils/nonce-provider.ts'
import { type loader } from './server.route.ts'

/* -------------------------------------------------------------------------------------------------
 * Layout
 * -----------------------------------------------------------------------------------------------*/

type LayoutProps = {
	children: React.ReactNode
	locale: Locale
	honeyProps: any
	env: any
	cart: GetOrder
}

const Layout: React.FC<LayoutProps> = ({
	children,
	locale,
	honeyProps,
	env,
	cart,
}) => {
	const { i18n } = useTranslation()

	const nonce = useNonce()
	const isBot = useIsBot()

	return (
		<HoneypotProvider {...honeyProps}>
			<Toast.Provider multiple={true}>
				<CartProvider initialCart={cart}>
					<html lang={locale} dir={i18n.dir()}>
          <head>
            <ClientHintCheck nonce={nonce}/>
            <meta charSet="utf-8"/>
            <meta
              name="viewport"
              content="width=device-width,initial-scale=1"
            />
            <meta
              httpEquiv="Content-Security-Policy"
              content="upgrade-insecure-requests"
            />

            <link
              rel="mask-icon"
              href="/pwa/favicon/safari-pinned-tab.svg"
              color="#ffffff"
            />
            <link rel="shortcut icon" href="/pwa/favicon/favicon.ico"/>
            <link
              rel="apple-touch-icon-precomposed"
              href="/pwa/logo/logo_color_text.png"
            />
            <meta name="application-name" content={appConfig.seo.title}/>
            <MsApplication/>
            <AppleMobileWebApp/>
            <FavIcon/>
            <meta name="theme-color" content="#ffffff"/>
            <meta
              name="revisit-after"
              content={appConfig.seo.revisit_after}
            />
            <meta itemProp="copyrightHolder" content={appConfig.seo.title}/>
            <meta
              itemProp="copyright_year"
              content={appConfig.seo.copyright_year}
            />
            <meta itemProp="isFamilyFriendly" content="true"/>
            <meta itemProp="image" content="/pwa/logo/logo_color_text.png"/>
            <meta name="mobile-web-app-capable" content="yes"/>

            <Meta/>
            <ManifestLink/>
            <Links/>
          </head>
          <body>
          {children}
          <script
            nonce={nonce}
								dangerouslySetInnerHTML={{
									__html: `window.ENV = ${JSON.stringify(env)}`,
								}}
							/>
							<ScrollRestoration nonce={nonce} />
							{!isBot && <Scripts nonce={nonce} />}
						</body>
					</html>
				</CartProvider>
			</Toast.Provider>
		</HoneypotProvider>
	)
}

/* -------------------------------------------------------------------------------------------------
 * App
 * -----------------------------------------------------------------------------------------------*/

const App: React.FC = () => {
	const {
		cart,
		locale,
		loggedIn,
		honeyProps,
		ENV: env,
	} = useLoaderData<typeof loader>() || {
		locale: i18nConfig.defaultLng,
		ENV: {},
	}

	const [app, setApp] = React.useState<App>({
		displaySidebar: false,
		displayModal: false,
		modalView: ROOT_APP_VIEW.loginView,
		sidebarView: ROOT_APP_VIEW.cartView,
		options: {
			redirect: null,
		},
	})

	moment.locale(
		(() => {
			switch (locale) {
				case 'zh':
					return 'zh-cn'
				default:
					return locale
			}
		})(),
	)

	useSWEffect()

	useChangeLanguage(locale)

	return (
		<Layout locale={locale} honeyProps={honeyProps} env={env} cart={cart}>
			<Theme
				appearance="light"
				accentColor="grass"
				radius="medium"
				scaling="100%"
				panelBackground="solid"
			>
				<Flex
					direction="column"
					position="relative"
					minWidth="100vw"
					minHeight="100vh"
				>
					<Navbar locale={locale} loggedIn={loggedIn!} setApp={setApp} />
					<Flex direction="column" flexGrow="1">
						<Outlet
							context={{
								app,
								locale,
								loggedIn,
								setApp,
							}}
						/>
					</Flex>
					<Footer app={app} locale={locale} setApp={setApp} />

					<ToastContainer />
					<SidebarUI app={app} locale={locale} setApp={setApp} />
					<ModalUI app={app} locale={locale} setApp={setApp} />
				</Flex>
				<Toast.Viewport />
				{process.env.NODE_ENV === 'development' && false && <ThemePanel />}
				<Toast.Viewport />
			</Theme>
		</Layout>
	)
}

/* -----------------------------------------------------------------------------------------------*/

export default withSentry(App)
export { type App }
